import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import {
  Alert, Button, Dialog, DialogContent, Field, Input, OtpInput, ToggleGroup, ToggleGroupItem,
} from '@datanomik/core-ui';
import { Key2Line, MailLine } from '@datanomik/core-ui/icons';

import type { PasswordResetChannel } from '../api';
import { usePasswordReset } from '../hooks/usePasswordReset';
import { isWhatsAppResetEnabled } from '../lib/passwordResetChannels';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const CODE_LENGTH = 6;

interface PasswordResetDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function PasswordResetDialog({ open, onOpenChange }: PasswordResetDialogProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { busy, error, sent, submit, verifyCode, reset } = usePasswordReset();
  const [channel, setChannel] = useState<PasswordResetChannel>('EMAIL');
  const [code, setCode] = useState('');
  const [opening, setOpening] = useState(false);
  const whatsAppEnabled = isWhatsAppResetEnabled();
  const byWhatsApp = whatsAppEnabled && channel === 'WHATSAPP';
  const { register, handleSubmit, formState } = useForm<{ email: string }>({
    defaultValues: { email: '' },
  });

  useEffect(() => {
    if (sent && byWhatsApp) import('../pages/ResetPasswordPage');
  }, [sent, byWhatsApp]);

  const close = (next: boolean) => {
    if (!next) {
      reset();
      setChannel('EMAIL');
      setCode('');
    }
    onOpenChange(next);
  };

  const confirmCode = async () => {
    if (busy || opening || code.length !== CODE_LENGTH) return;
    const recoveryToken = await verifyCode(code);
    if (!recoveryToken) {
      setCode('');
      return;
    }
    setOpening(true);
    navigate(`/reset-password/${recoveryToken}`);
  };

  const footer = () => {
    if (sent && byWhatsApp) {
      return (
        <Button className="w-full" loading={busy || opening} disabled={code.length !== CODE_LENGTH} onClick={confirmCode}>
          {t('Continue')}
        </Button>
      );
    }
    if (sent) return <Button className="w-full" onClick={() => close(false)}>{t('Close')}</Button>;
    return (
      <Button type="submit" form="password-reset-form" className="w-full" loading={busy}>
        {byWhatsApp ? t('Send me a code') : t('Send me a reset link')}
      </Button>
    );
  };

  const body = () => {
    if (sent && byWhatsApp) {
      return (
        <div className="flex flex-col gap-3">
          {error && <Alert tone="destructive">{error}</Alert>}
          <OtpInput
            value={code}
            onChange={setCode}
            length={CODE_LENGTH}
            invalid={Boolean(error)}
            autoFocus
            label={t('Verification code')}
            onComplete={confirmCode}
          />
        </div>
      );
    }
    if (sent) {
      return (
        <Alert tone="success" title={t('Reset link sent!')}>
          {t('Help is on the way, please check your email for further instructions.')}
        </Alert>
      );
    }
    return null;
  };

  const description = () => {
    if (sent && byWhatsApp) {
      return t('Enter the 6-digit code we sent to your WhatsApp. If your mobile is not verified, we sent a link to your email instead.');
    }
    return byWhatsApp
      ? t('Enter your email and we will send a code by WhatsApp to the mobile you verified in your profile.')
      : t('Enter your email and we will send you a link to choose a new one.');
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent
        icon={<Key2Line />}
        title={t('Forgot your password?')}
        description={description()}
        footer={footer()}
      >
        {sent ? body() : (
          <form
            id="password-reset-form"
            onSubmit={handleSubmit(({ email }) => submit(email, byWhatsApp ? 'WHATSAPP' : 'EMAIL'))}
            noValidate
            className="flex flex-col gap-3"
          >
            {error && <Alert tone="destructive">{error}</Alert>}
            {whatsAppEnabled && (
              <Field label={t('Send it by')}>
                <ToggleGroup
                  aria-label={t('Send it by')}
                  value={channel}
                  onValueChange={(next) => setChannel(next as PasswordResetChannel)}
                  className="w-full"
                >
                  <ToggleGroupItem value="EMAIL" className="flex-1">{t('Email')}</ToggleGroupItem>
                  <ToggleGroupItem value="WHATSAPP" className="flex-1">WhatsApp</ToggleGroupItem>
                </ToggleGroup>
              </Field>
            )}
            <Field label={t('Email')} required error={formState.errors.email?.message}>
              <Input
                type="email"
                autoComplete="email"
                placeholder={t('you@company.com')}
                startAdornment={<MailLine aria-hidden="true" />}
                {...register('email', {
                  required: t('Email is required'),
                  pattern: { value: EMAIL_PATTERN, message: t('Please enter a valid email address') },
                })}
              />
            </Field>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
